import { useState, useRef, useEffect } from 'react';
import { Heart, Trophy, Users, Zap, Star, ArrowRight, Target, Shield, Activity, HandHeart, Wallet, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/App.css';
import highlight_1 from '@/assets/highlight_1.jpeg';
import RJD_Mokoena from '@/assets/members/RJD Mokoena.jpeg'; // Corrected path
import B_Radebe from '@/assets/members/B Radebe.jpeg'; // Corrected path
import N_Mosia from '@/assets/members/N Mosia.jpeg'; // Corrected path
import M_Mokoena from '@/assets/members/M Mokoena.jpeg'; // Corrected path

import Navigation from '@/components/nav';
import FooterSection from '@/components/footer';
import HeroSection from '@/components/HeroSection';

function AboutPage() {
    const sectionRef = useRef<HTMLElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 }
        );

        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, []);

    const stats = [
        { label: 'Total Participants', value: '185+', icon: Users },
        { label: 'Active Competitions', value: '3', icon: Trophy },
        { label: 'Total Votes', value: '500K+', icon: Heart },
        { label: 'Community Members', value: '10K+', icon: Star },
    ];

    const values = [
        {
            icon: Heart,
            title: 'Passion',
            description: 'We celebrate the passion and dedication that every participant brings to the stage.',
        },
        {
            icon: Zap,
            title: 'Excellence',
            description: 'We strive for the highest standards in organizing world-class talent competitions.',
        },
        {
            icon: Users,
            title: 'Community',
            description: 'We believe in building a supportive community that uplifts and celebrates talent.',
        },
        {
            icon: Trophy,
            title: 'Recognition',
            description: 'Every talent deserves to be recognized and celebrated on a prestigious platform.',
        },
    ];

    const coreObjectives = [
        { icon: Star, title: 'Talent Development', description: 'Promote and support youth and children with talent by advancing skills in sport, dance, singing, boxing, live performances, and awards programmes.' },
        { icon: Users, title: 'Community Involvement', description: 'Encourage active community participation in development initiatives through targeted awareness campaigns.' },
        { icon: Activity, title: 'Health Education', description: 'Educate the community and combat the transmission of HIV and AIDS.' },
        { icon: Zap, title: 'Motivation & Life Skills', description: 'Organise motivational talks that equip community members with strategies to manage daily challenges.' },
        { icon: Shield, title: 'Educational Support', description: 'Introduce programmes, in collaboration with relevant departments, that inspire teenagers to take greater interest in their education.' },
        { icon: HandHeart, title: 'Economic Empowerment', description: 'Identify out-of-school and unemployed youth, and assist them to initiate sustainable income-generating activities.' },
    ];

    const secondaryObjectives = [
        'Promote performing arts by encouraging youth participation in drama, dance, and music.',
        'Strengthen local sport by organising coaching and refereeing clinics across multiple codes, including soccer, tennis, netball, and rugby.',
        'Foster consistent training and healthy competition by establishing leagues, hosting sectional tournaments, and reintroducing indigenous and adopted games to the community.'
    ];

    const bankingDetails = {
        bank: 'Capitec Bank',
        accountName: 'Qwaqwa Talent Show',
        accountNo: '2528410898',
        branchCode: '470010',
        reference: 'Donation + Your Name',
        mobileEFT: '0678359550'
    };

    const contactInfo = {
        phone: '0678359550',
        email: 'qwa2talentshow@gmail.com'
    };

    const teamMembers = [
        {
            name: 'Mr. RJD Mokoena',
            role: 'Founder & Manager', // Changed role
            image: RJD_Mokoena,
            bio: 'A dedicated leader with a passion for empowering youth and fostering talent. Mr. Mokoena founded Qwaqwa Talent Show to provide a platform for aspiring artists and performers, guiding them to reach their full potential and make a lasting impact.', // Updated bio
        },
        {
            name: 'Ms. B Radebe',
            role: 'Public Awareness Manager',
            image: B_Radebe,
            bio: 'Passionate about disseminating valuable information to empower communities. Through her work, she provides insightful dialogues, thought-provoking debates, skills development programs, and educational initiatives. With a commitment to informing and educating the public, Ms. Radebe bridges knowledge gaps and fosters a more informed society.',
        },
        {
            name: 'Ms. N Mosia',
            role: 'Creative Director',
            image: N_Mosia,
            bio: 'An award-winning creative strategist and designer, known for her innovative approach to visual storytelling. With a keen eye for aesthetics and a passion for pushing creative boundaries, she has successfully crafted compelling campaigns and designs that resonate with audiences. Her work has garnered recognition for its originality and impact, making her a sought-after creative mind in the industry.',
        },
        {
            name: 'Mrs. M Mokoena',
            role: 'Talent Manager', // Changed role
            image: M_Mokoena,
            bio: 'Dedicated to discovering and showcasing exceptional talents through his innovative talent shows. With a keen eye for spotting hidden gems, he provides a platform for aspiring artists and performers to shine, empowering them to reach their full potential. Through his work, Mrs. Mokoena inspires and nurtures the next generation of stars, leaving a lasting impact on the entertainment industry.',
        },
    ];

    const milestones = [
        { year: '2022', event: 'Qwaqwa Talent Show Founded' },
        { year: '2023', event: 'First International Participants' },
        { year: '2024', event: 'Expanded to 3 Major Competitions' },
        { year: '2025', event: 'Reached 500K+ Total Votes' },
        { year: '2026', event: 'Launched Online Voting Platform' },
    ];

    return (
        <div className="min-h-screen bg-white">
            <Navigation />
            <main>
                {/* HERO SECTION */}
                <HeroSection title='Our Story' description='About Us' long='Empowering talent, celebrating excellence, and building a vibrant community of artists and competitors from around the world.' />
            

                {/* NPO INFO STRIP */}
                <div className="bg-QWA2_TALENT_SHOW-black py-4 border-y border-white/10">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-12 text-white/80 text-xs md:text-sm font-bold uppercase tracking-widest">
                            <span className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-QWA2_TALENT_SHOW-gold" /> Registered Non-Profit Organisation
                            </span>
                            <span className="hidden md:block w-1.5 h-1.5 rounded-full bg-QWA2_TALENT_SHOW-gold" />
                            <span>Founded 16 July 2022</span>
                            <span className="hidden md:block w-1.5 h-1.5 rounded-full bg-QWA2_TALENT_SHOW-gold" />
                            <span>Founder & Manager: Mr RJD Mokoena</span>
                        </div>
                    </div>
                </div>

             

                {/* OUR STORY SECTION */}
                <section className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
                    <div className="absolute top-20 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-red/20 blur-3xl" />
                    <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-16 text-center">
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">
                                Our Story
                            </h2>
                            <p className="mt-4 text-base md:text-lg text-QWA2_TALENT_SHOW-gray max-w-2xl mx-auto">
                                From a small vision to a thriving platform for talent
                            </p>
                        </div>

                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            {/* TEXT */}
                            <div className="space-y-6">
                                <p className="text-lg text-QWA2_TALENT_SHOW-black leading-relaxed">
                                    For the past 5 years, Qwa2 Talent Show has been a trusted platform dedicated to empowering youth and teenagers by showcasing and developing their talents.
                                </p>
                                <p className="text-lg text-QWA2_TALENT_SHOW-black leading-relaxed">
                                    We believe that when young people are given opportunity, guidance, and recognition, they are less likely to be drawn into crime, substance abuse, teenage pregnancy, and other social challenges caused by peer pressure and limited support.
                                </p>
                                <p className="text-lg text-QWA2_TALENT_SHOW-black leading-relaxed">
                                    Through our initiatives, Qwa2 Talent Show continues to build a safer, more hopeful future for the youth of Qwaqwa and beyond.
                                </p>
                                {/* <div className="pt-4">
                                    <Button className="bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold text-white font-bold uppercase tracking-wider py-3 px-8 rounded-2xl hover:opacity-90 transition flex items-center gap-2">
                                        Learn More <ArrowRight className="w-5 h-5" />
                                    </Button>
                                </div> */}
                            </div>

                            {/* IMAGE */}
                            <div className="rounded-3xl overflow-hidden shadow-2xl">
                                <img src={highlight_1} alt="Our story" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                </section>

                {/* MISSION & VISION */}
                <section className="relative py-20 md:py-28 bg-white overflow-hidden">
                    <div className="absolute top-10 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
                    <div className="absolute bottom-10 left-10 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-2 gap-8">
                            {/* MISSION */}
                            <div className="rounded-3xl bg-gradient-to-br from-QWA2_TALENT_SHOW-red/10 to-transparent p-8 md:p-12 border border-QWA2_TALENT_SHOW-red/20">
                                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-QWA2_TALENT_SHOW-red/20 mb-6">
                                    <Heart className="w-8 h-8 text-QWA2_TALENT_SHOW-red" />
                                </div>
                                <h3 className="text-3xl font-black text-QWA2_TALENT_SHOW-black mb-4">Our Mission</h3>
                                <p className="text-lg text-QWA2_TALENT_SHOW-gray leading-relaxed">
                                    To transform the lives of youth and teenagers, alongside the broader community, by creating an inclusive environment where everyone can thrive equally.
                                </p>
                            </div>

                            {/* VISION */}
                            <div className="rounded-3xl bg-gradient-to-br from-QWA2_TALENT_SHOW-gold/10 to-transparent p-8 md:p-12 border border-QWA2_TALENT_SHOW-gold/20">
                                <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-QWA2_TALENT_SHOW-gold/20 mb-6">
                                    <Target className="w-8 h-8 text-QWA2_TALENT_SHOW-gold" />
                                </div>
                                <h3 className="text-3xl font-black text-QWA2_TALENT_SHOW-black mb-4">Our Goal</h3>
                                <p className="text-lg text-QWA2_TALENT_SHOW-gray leading-relaxed">
                                    To provide comprehensive support and protection that keeps young people engaged in positive activities, ensuring they remain focused on education, sport, and personal talent development.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* OBJECTIVES SECTION */}
                <section className="relative py-20 md:py-28 bg-gray-50 overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">Core Objectives</h2>
                            <div className="w-20 h-1.5 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold mt-4 rounded-full" />
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
                            {coreObjectives.map((obj, i) => (
                                <div key={i} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
                                    <obj.icon className="w-10 h-10 text-QWA2_TALENT_SHOW-gold mb-4" />
                                    <h4 className="text-xl font-bold text-QWA2_TALENT_SHOW-black mb-2">{obj.title}</h4>
                                    <p className="text-QWA2_TALENT_SHOW-gray text-sm leading-relaxed">{obj.description}</p>
                                </div>
                            ))}
                        </div>

                        <div className="mb-16">
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">Secondary Objectives</h2>
                            <div className="w-20 h-1.5 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold mt-4 rounded-full" />
                        </div>

                        <div className="grid md:grid-cols-3 gap-6">
                            {secondaryObjectives.map((text, i) => (
                                <div key={i} className="flex gap-4 items-start bg-white/50 p-6 rounded-3xl border border-gray-200">
                                    <div className="w-6 h-6 rounded-full bg-QWA2_TALENT_SHOW-red/20 flex items-center justify-center shrink-0 mt-1">
                                        <span className="text-QWA2_TALENT_SHOW-red font-bold text-xs">{i + 1}</span>
                                    </div>
                                    <p className="text-QWA2_TALENT_SHOW-black font-medium leading-relaxed">{text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* HOW YOU CAN HELP / DONATION & VOLUNTEER CARDS */}
                <section className="relative py-20 md:py-28 bg-white overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">How You Can Help</h2>
                        <p className="mt-4 text-QWA2_TALENT_SHOW-gray text-lg">Your support keeps our platform alive and our youth motivated</p>
                    </div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
                        {/* DONATION CARD */}
                        <div className="rounded-[2.5rem] bg-gradient-to-br from-QWA2_TALENT_SHOW-gold/20 to-white p-8 md:p-12 border border-QWA2_TALENT_SHOW-gold/30 shadow-xl relative overflow-hidden group">
                            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                                <Wallet className="w-32 h-32" />
                            </div>
                            <h3 className="text-3xl font-black text-QWA2_TALENT_SHOW-black mb-6">Donate Now</h3>
                            <p className="text-QWA2_TALENT_SHOW-gray mb-8 leading-relaxed">
                                Your donation keeps our shows, workshops, tournaments, and school programmes running. Every R10 buys a meal for a performer. Every R100 helps with transport, costumes, sound, and prizes.
                            </p>
                            
                            <div className="space-y-4 bg-white/60 backdrop-blur-sm p-6 rounded-3xl border border-white">
                                <div className="grid grid-cols-2 gap-y-3 text-sm">
                                    <span className="font-bold text-QWA2_TALENT_SHOW-gray uppercase">Bank</span>
                                    <span className="font-black text-right text-QWA2_TALENT_SHOW-black">{bankingDetails.bank}</span>
                                    <span className="font-bold text-QWA2_TALENT_SHOW-gray uppercase">Account Name</span>
                                    <span className="font-black text-right text-QWA2_TALENT_SHOW-black">{bankingDetails.accountName}</span>
                                    <span className="font-bold text-QWA2_TALENT_SHOW-gray uppercase">Account No.</span>
                                    <span className="font-black text-right text-QWA2_TALENT_SHOW-gold">{bankingDetails.accountNo}</span>
                                    <span className="font-bold text-QWA2_TALENT_SHOW-gray uppercase">Branch Code</span>
                                    <span className="font-black text-right text-QWA2_TALENT_SHOW-black">{bankingDetails.branchCode}</span>
                                    <span className="font-bold text-QWA2_TALENT_SHOW-gray uppercase">Reference</span>
                                    <span className="font-black text-right text-QWA2_TALENT_SHOW-black">{bankingDetails.reference}</span>
                                </div>
                                <div className="pt-4 border-t border-gray-200">
                                    <p className="text-xs font-bold text-QWA2_TALENT_SHOW-gray uppercase mb-1">Mobile/EFT Options</p>
                                    <p className="text-xl font-black text-QWA2_TALENT_SHOW-red">{bankingDetails.mobileEFT}</p>
                                </div>
                            </div>
                        </div>

                        {/* VOLUNTEER / SPONSOR CARD */}
                        <div className="rounded-[2.5rem] bg-gradient-to-br from-QWA2_TALENT_SHOW-red/20 to-white p-8 md:p-12 border border-QWA2_TALENT_SHOW-red/30 shadow-xl relative overflow-hidden group">
                             <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                                <Users className="w-32 h-32" />
                            </div>
                            <h3 className="text-3xl font-black text-QWA2_TALENT_SHOW-black mb-6">Join or Volunteer</h3>
                            <p className="text-QWA2_TALENT_SHOW-gray mb-8 leading-relaxed">
                                For sponsorships or in-kind donations (sound, transport, food, prizes), or if you want to join our volunteer team, we'd love to hear from you.
                            </p>

                            <div className="space-y-6">
                                <div className="flex items-center gap-4 bg-white/60 p-4 rounded-2xl border border-white">
                                    <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                                        <MessageSquare className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-QWA2_TALENT_SHOW-gray uppercase">WhatsApp "Hi"</p>
                                        <p className="text-lg font-black text-QWA2_TALENT_SHOW-black">{contactInfo.phone}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 bg-white/60 p-4 rounded-2xl border border-white">
                                    <div className="w-12 h-12 rounded-xl bg-QWA2_TALENT_SHOW-gold/10 flex items-center justify-center">
                                        <HandHeart className="w-6 h-6 text-QWA2_TALENT_SHOW-gold" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-QWA2_TALENT_SHOW-gray uppercase">Email Us</p>
                                        <p className="text-lg font-black text-QWA2_TALENT_SHOW-black">{contactInfo.email}</p>
                                    </div>
                                </div>

                              
                            </div>
                        </div>
                    </div>
                </section>

                {/* CORE VALUES */}
                <section className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-100 overflow-hidden">
                    <div className="absolute top-24 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-red/20 blur-3xl" />
                    <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-16 text-center">
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">
                                Our Core Values
                            </h2>
                            <p className="mt-4 text-base md:text-lg text-QWA2_TALENT_SHOW-gray max-w-2xl mx-auto">
                                The principles that guide everything we do
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {values.map((value, index) => {
                                const Icon = value.icon;
                                return (
                                    <div
                                        key={index}
                                        className="rounded-3xl bg-white p-8 border border-QWA2_TALENT_SHOW-gold/20 hover:border-QWA2_TALENT_SHOW-gold/50 hover:shadow-lg transition-all text-center"
                                    >
                                        <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-QWA2_TALENT_SHOW-gold/20 mx-auto mb-4">
                                            <Icon className="w-8 h-8 text-QWA2_TALENT_SHOW-gold" />
                                        </div>
                                        <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-black mb-3">{value.title}</h3>
                                        <p className="text-QWA2_TALENT_SHOW-gray">{value.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* TEAM SECTION */}
                <section ref={sectionRef} className="relative py-20 md:py-28 bg-white overflow-hidden">
                    <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/10 blur-3xl" />
                    <div className="absolute bottom-10 right-10 w-32 h-32 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className={`mb-16 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">
                                Meet Our Leadership Team
                            </h2>
                            <p className="mt-4 text-base md:text-lg text-QWA2_TALENT_SHOW-gray max-w-2xl mx-auto">
                                Passionate professionals dedicated to making talent show magic happen
                            </p>
                        </div>

                        <div className="grid md:grid-cols-4 gap-8">
{teamMembers.map((member, index) => (
  <div
    key={index}
    className={`group relative overflow-hidden rounded-[36px] border border-white/10 bg-gradient-to-b from-white to-[#faf7f2] shadow-[0_15px_50px_rgba(0,0,0,0.08)] hover:shadow-[0_25px_80px_rgba(0,0,0,0.16)] hover:-translate-y-3 transition-all duration-700 ${
      isVisible
        ? 'opacity-100 translate-y-0'
        : 'opacity-0 translate-y-12'
    }`}
    style={{ transitionDelay: `${index * 120}ms` }}
  >
    {/* Animated Glow */}
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
      <div className="absolute -top-20 -left-20 w-52 h-52 bg-QWA2_TALENT_SHOW-red/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-20 -right-20 w-52 h-52 bg-QWA2_TALENT_SHOW-gold/10 rounded-full blur-3xl" />
    </div>

    {/* Image Section */}
    {member.image && (
      <div className="relative aspect-[4/4.8] overflow-hidden">
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
        />

        {/* Premium Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

       

        {/* Floating Name */}
        <div className="absolute bottom-5 left-5 right-5">
          <h3 className="text-1xl md:text-2xl font-black text-white leading-tight">
            {member.name}
          </h3>

          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15">
            <div className="w-2 h-2 rounded-full bg-QWA2_TALENT_SHOW-gold animate-pulse" />

            <p className="text-white text-xs font-bold uppercase tracking-[0.05em]">
              {member.role}
            </p>
          </div>
        </div>
      </div>
    )}

    {/* Content */}
    <div className="relative p-7">
      {/* Decorative Top */}
      <div className="flex items-center gap-3 mb-5">
        <div className="h-[2px] w-12 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold rounded-full" />

        <span className="text-[10px] uppercase tracking-[0.35em] font-bold text-QWA2_TALENT_SHOW-gold">
          Team Member
        </span>
      </div>

      {/* Bio */}
      {member.bio && (
        <p className="text-QWA2_TALENT_SHOW-gray text-sm md:text-[15px] leading-relaxed">
          {member.bio}
        </p>
      )}
        </div>
  </div>
))}
                        </div>
                    </div>
                </section>

                {/* TIMELINE/MILESTONES */}
                <section className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
                    <div className="absolute top-20 right-10 w-40 h-40 rounded-full bg-QWA2_TALENT_SHOW-gold/20 blur-3xl" />
                    <div className="absolute bottom-20 left-10 w-48 h-48 rounded-full bg-QWA2_TALENT_SHOW-red/10 blur-3xl" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="mb-16 text-center">
                            <h2 className="text-3xl md:text-4xl font-black text-QWA2_TALENT_SHOW-black">
                                Our Journey
                            </h2>
                            <p className="mt-4 text-base md:text-lg text-QWA2_TALENT_SHOW-gray max-w-2xl mx-auto">
                                Key milestones in our growth and evolution
                            </p>
                        </div>

                        <div className="space-y-6 max-w-3xl mx-auto">
                            {milestones.map((milestone, index) => (
                                <div key={index} className="flex gap-6 items-start">
                                    <div className="flex-shrink-0 flex items-center justify-center">
                                        <div className="relative flex items-center justify-center">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-QWA2_TALENT_SHOW-gold to-QWA2_TALENT_SHOW-red flex items-center justify-center">
                                                <span className="text-white font-black text-lg">{index + 1}</span>
                                            </div>
                                            {index < milestones.length - 1 && (
                                                <div className="absolute top-12 w-1 h-[80px] bg-gradient-to-b from-QWA2_TALENT_SHOW-gold to-transparent" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1 rounded-3xl bg-white p-6 border border-QWA2_TALENT_SHOW-gold/20">
                                        <h3 className="text-2xl font-black text-QWA2_TALENT_SHOW-gold">{milestone.year}</h3>
                                        <p className="text-lg text-QWA2_TALENT_SHOW-black font-semibold mt-2">{milestone.event}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA SECTION */}
                <section className="relative py-20 md:py-28 bg-gradient-to-r from-QWA2_TALENT_SHOW-red to-QWA2_TALENT_SHOW-gold overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-10 left-10 w-40 h-40 rounded-full bg-white blur-3xl" />
                        <div className="absolute bottom-10 right-10 w-48 h-48 rounded-full bg-black blur-3xl" />
                    </div>

                    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl md:text-5xl font-black text-white mb-6">
                            Ready to Showcase Your Talent?
                        </h2>
                        <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                            Join thousands of talented individuals worldwide. Whether you're a performer, athlete, or artist, find your competition and register today.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button className="bg-white text-QWA2_TALENT_SHOW-red font-bold uppercase tracking-wider py-3 px-8 rounded-2xl hover:bg-gray-50 transition">
                                Explore Competitions
                            </Button>
                            <Button className="bg-white/20 border-2 border-white text-white font-bold uppercase tracking-wider py-3 px-8 rounded-2xl hover:bg-white/30 transition">
                                Contact Us
                            </Button>
                        </div>
                    </div>
                </section>

                <FooterSection />
            </main>
        </div>
    );
}

export default AboutPage;
